import { EPLATFORM } from '../constants/platform'
import { IPlatformLink, IUsedPlatformLink } from '../types/product'

// key ของ SKU marketplace — ใช้ `skuId` ก่อนเสมอ เพราะสินค้าตัวเดียวกันคนละร้าน
// มี seller_sku ซ้ำกันได้ (เช่น J-KY-30 อยู่ 2 ร้าน คนละ sku_id) ถ้าเทียบด้วย sku จะจับผิดตัว
// ตกไป sku เฉพาะตอนไม่มี skuId (ผูกมือ / สินค้าไม่มีตัวเลือก); sku ว่างถือว่าไม่มี key
export const platformLinkKey = (link: {
  skuId?: string
  sku?: string
}): string | null => {
  if (link.skuId) {
    return link.skuId
  }
  const sku = link.sku?.trim()
  return sku ? `sku:${sku}` : null
}

// ลง key ทั้ง skuId และ sku ของ link เดียวกัน — ตัวเลือกใน dropdown จับด้วย skuId
// ส่วนช่องกรอกเองไม่มี skuId จับได้แค่ sku; resolver ตัดสต๊อกก็ค้นทั้ง 2 ชั้นเหมือนกัน
// (ผูกมือ sku ซ้ำกับ link ที่มี skuId อยู่แล้ว = ชั้น sku เจอ 2 สินค้า → ambiguous)
const setLinkKeys = (
  map: Map<string, string>,
  link: { skuId?: string; sku?: string },
  label: string,
): void => {
  const sku = link.sku?.trim()
  if (link.skuId) {
    map.set(link.skuId, label)
  }
  if (sku) {
    map.set(`sku:${sku}`, label)
  }
}

// map key → ข้อความบอกว่าใครใช้อยู่ สำหรับปิดตัวเลือกใน dropdown ผูกสินค้า
// - ของสินค้าตัวอื่น (จาก server) → บอกชื่อสินค้านั้น
// - ของสินค้าที่กำลังแก้อยู่ (state ใน modal ยังไม่บันทึกก็นับ) → บอกว่าผูกกับตัวนี้แล้ว
export const buildUsedLinkMap = (
  usedLinks: IUsedPlatformLink[],
  currentLinks: IPlatformLink[],
  currentProductObjectId?: string,
): Map<string, string> => {
  const map = new Map<string, string>()

  usedLinks.forEach((link) => {
    if (link.productObjectId === currentProductObjectId) {
      return
    }
    setLinkKeys(
      map,
      link,
      `ผูกแล้วกับ ${link.productName || link.productCode || '-'}`,
    )
  })

  currentLinks.forEach((link) => {
    setLinkKeys(map, link, 'ผูกกับสินค้านี้แล้ว')
  })

  return map
}

// SKU TikTok ที่จะผูก — มาจากแถวที่กด ผู้ใช้ไม่ต้องกรอกเอง
export interface ISkuLinkTarget {
  shopId?: string
  shopName?: string
  productId?: string // product_id ฝั่ง TikTok
  productName?: string
  skuId?: string
  sku?: string // seller_sku
}

export const buildTiktokSkuLink = (target: ISkuLinkTarget): IPlatformLink => ({
  platform: EPLATFORM.TIKTOK,
  shopId: target.shopId || undefined,
  shopName: target.shopName || undefined,
  platformId: target.productId || undefined,
  productName: target.productName || undefined,
  skuId: target.skuId || undefined,
  sku: target.sku || undefined,
  linkedBy: 'manual',
})

// SKU นี้ถูกสินค้าตัวไหนผูกไว้แล้วบ้าง — ผูกซ้ำ = ตัดสต๊อก/หาต้นทุนไม่ได้ทั้งคู่
// link ที่ไม่มี skuId (ผูกด้วย seller_sku) นับเฉพาะร้านเดียวกัน
// เพราะ seller_sku ซ้ำข้ามร้านได้
export const findLinkConflict = (
  usedLinks: IUsedPlatformLink[],
  target: ISkuLinkTarget,
): IUsedPlatformLink | undefined =>
  usedLinks.find((used) => {
    if (used.platform !== EPLATFORM.TIKTOK) {
      return false
    }
    if (used.skuId && target.skuId) {
      return used.skuId === target.skuId
    }
    const sku = target.sku?.trim()
    return (
      !!sku &&
      used.sku?.trim() === sku &&
      (!used.shopId || !target.shopId || used.shopId === target.shopId)
    )
  })
